import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Supplier, SupplierFormData, formDataToSupplier, supplierToFormData } from "@/types/contacts";
import { toast } from "sonner";

const SUPPLIERS_QUERY_KEY = ["suppliers"];

// Função para buscar todos os fornecedores
async function fetchSuppliers(): Promise<Supplier[]> {
  console.log('🔍 [FETCH SUPPLIERS] Iniciando busca de fornecedores...');

  const { data, error } = await supabase
    .from("suppliers")
    .select("*")
    .order("name", { ascending: true });

  if (error) {
    console.error("❌ [FETCH SUPPLIERS] Error fetching suppliers:", error);
    throw error;
  }

  console.log('🔍 [FETCH SUPPLIERS] Dados brutos do Supabase:', data);
  console.log('📊 [FETCH SUPPLIERS] Quantidade de fornecedores:', data?.length || 0);

  // Transformar dados do banco (snake_case) para o formato Supplier (camelCase)
  const transformedSuppliers = (data || []).map(dbSupplier => ({
    id: dbSupplier.id,
    userId: dbSupplier.user_id,
    name: dbSupplier.name,
    documentType: dbSupplier.document_type,
    documentNumber: dbSupplier.document_number,
    email: dbSupplier.email,
    phone: dbSupplier.phone,
    address: {
      street: dbSupplier.address_street,
      number: dbSupplier.address_number,
      complement: dbSupplier.address_complement,
      neighborhood: dbSupplier.address_neighborhood,
      city: dbSupplier.address_city,
      state: dbSupplier.address_state,
      zipCode: dbSupplier.address_zip_code,
    },
    bankInfo: {
      bankName: dbSupplier.bank_name,
      agency: dbSupplier.bank_agency,
      account: dbSupplier.bank_account,
      accountType: dbSupplier.bank_account_type,
    },
    paymentTerms: dbSupplier.payment_terms,
    notes: dbSupplier.notes,
    isActive: dbSupplier.is_active,
    createdAt: dbSupplier.created_at,
    updatedAt: dbSupplier.updated_at,
  }));

  console.log('✨ [FETCH SUPPLIERS] Dados transformados:', transformedSuppliers);

  return transformedSuppliers;
}

// Função para buscar um fornecedor pelo ID
async function fetchSupplierById(id: string): Promise<Supplier> {
  const { data, error } = await supabase
    .from("suppliers")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error("Error fetching supplier:", error);
    throw error;
  }

  return data;
}

function normalizeBankAccountType(
  type?: string | null
): 'corrente' | 'poupanca' | null {
  if (!type) return null;
  const normalized = type.trim().toLowerCase();
  return normalized === 'corrente' || normalized === 'poupanca' ? normalized : null;
}

// Função para criar um novo fornecedor
async function createSupplier(formData: SupplierFormData, userId: string): Promise<Supplier> {
  const supplierData = formDataToSupplier(formData, userId);

  // Transformar o objeto aninhado para o formato do banco de dados
  const dbData = {
    user_id: supplierData.userId,
    name: supplierData.name,
    document_type: supplierData.documentType,
    document_number: supplierData.documentNumber?.trim() || `EMPTY-PLACEHOLDER-${Date.now()}`,
    email: supplierData.email,
    phone: supplierData.phone,
    address_street: supplierData.address?.street,
    address_number: supplierData.address?.number,
    address_complement: supplierData.address?.complement,
    address_neighborhood: supplierData.address?.neighborhood,
    address_city: supplierData.address?.city,
    address_state: supplierData.address?.state,
    address_zip_code: supplierData.address?.zipCode,
    bank_name: supplierData.bankInfo?.bankName,
    bank_agency: supplierData.bankInfo?.agency,
    bank_account: supplierData.bankInfo?.account,
    bank_account_type: normalizeBankAccountType(supplierData.bankInfo?.accountType),
    payment_terms: supplierData.paymentTerms,
    notes: supplierData.notes,
    is_active: supplierData.isActive,
  };

  const { data, error } = await supabase
    .from("suppliers")
    .insert([dbData])
    .select()
    .single();

  if (error) {
    console.error("Error creating supplier:", error);
    throw error;
  }

  // Transformar de volta para o formato Supplier
  return {
    id: data.id,
    userId: data.user_id,
    name: data.name,
    documentType: data.document_type,
    documentNumber: data.document_number,
    email: data.email,
    phone: data.phone,
    address: {
      street: data.address_street,
      number: data.address_number,
      complement: data.address_complement,
      neighborhood: data.address_neighborhood,
      city: data.address_city,
      state: data.address_state,
      zipCode: data.address_zip_code,
    },
    bankInfo: {
      bankName: data.bank_name,
      agency: data.bank_agency,
      account: data.bank_account,
      accountType: data.bank_account_type,
    },
    paymentTerms: data.payment_terms,
    notes: data.notes,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

// Função para atualizar um fornecedor
async function updateSupplier(id: string, formData: SupplierFormData): Promise<Supplier> {
  const supplierData = formDataToSupplier(formData, ""); // userId não é necessário para update

  // Transformar o objeto aninhado para o formato do banco de dados
  const dbData = {
    name: supplierData.name,
    document_type: supplierData.documentType,
    document_number: supplierData.documentNumber,
    email: supplierData.email,
    phone: supplierData.phone,
    address_street: supplierData.address?.street,
    address_number: supplierData.address?.number,
    address_complement: supplierData.address?.complement,
    address_neighborhood: supplierData.address?.neighborhood,
    address_city: supplierData.address?.city,
    address_state: supplierData.address?.state,
    address_zip_code: supplierData.address?.zipCode,
    bank_name: supplierData.bankInfo?.bankName,
    bank_agency: supplierData.bankInfo?.agency,
    bank_account: supplierData.bankInfo?.account,
    bank_account_type: normalizeBankAccountType(supplierData.bankInfo?.accountType),
    payment_terms: supplierData.paymentTerms,
    notes: supplierData.notes,
    is_active: supplierData.isActive,
  };

  const { data, error } = await supabase
    .from("suppliers")
    .update(dbData)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating supplier:", error);
    throw error;
  }

  // Transformar de volta para o formato Supplier
  return {
    id: data.id,
    userId: data.user_id,
    name: data.name,
    documentType: data.document_type,
    documentNumber: data.document_number,
    email: data.email,
    phone: data.phone,
    address: {
      street: data.address_street,
      number: data.address_number,
      complement: data.address_complement,
      neighborhood: data.address_neighborhood,
      city: data.address_city,
      state: data.address_state,
      zipCode: data.address_zip_code,
    },
    bankInfo: {
      bankName: data.bank_name,
      agency: data.bank_agency,
      account: data.bank_account,
      accountType: data.bank_account_type,
    },
    paymentTerms: data.payment_terms,
    notes: data.notes,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

// Função para deletar um fornecedor
async function deleteSupplier(id: string): Promise<void> {
  const { error } = await supabase
    .from("suppliers")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting supplier:", error);
    throw error;
  }
}

// Hook personalizado para gerenciar fornecedores
export function useSuppliers() {
  const queryClient = useQueryClient();

  // Query para buscar todos os fornecedores
  const suppliersQuery = useQuery({
    queryKey: SUPPLIERS_QUERY_KEY,
    queryFn: fetchSuppliers,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Query para buscar um fornecedor específico
  const useSupplier = (id: string) => {
    return useQuery({
      queryKey: [...SUPPLIERS_QUERY_KEY, id],
      queryFn: () => fetchSupplierById(id),
      enabled: !!id,
    });
  };

  // Mutation para criar fornecedor
  const createSupplierMutation = useMutation({
    mutationFn: ({ formData, userId }: { formData: SupplierFormData; userId: string }) =>
      createSupplier(formData, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
      toast.success("Fornecedor criado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Error creating supplier:", error);
      
      // Tratar erro de duplicidade de documento
      if (error.code === '23505' && error.message.includes('suppliers_document_number_key')) {
        toast.error("Já existe um fornecedor com este documento. Verifique e tente novamente.");
      } else {
        toast.error("Erro ao criar fornecedor. Tente novamente.");
      }
    },
  });

  // Mutation para atualizar fornecedor
  const updateSupplierMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: SupplierFormData }) =>
      updateSupplier(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
      toast.success("Fornecedor atualizado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Error updating supplier:", error);
      toast.error("Erro ao atualizar fornecedor. Tente novamente.");
    },
  });

  // Mutation para deletar fornecedor
  const deleteSupplierMutation = useMutation({
    mutationFn: deleteSupplier,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
      toast.success("Fornecedor deletado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Error deleting supplier:", error);
      toast.error("Erro ao deletar fornecedor. Tente novamente.");
    },
  });

  return {
    // Queries
    suppliers: suppliersQuery.data || [],
    isLoading: suppliersQuery.isLoading,
    error: suppliersQuery.error,
    useSupplier,

    // Mutations
    createSupplier: createSupplierMutation.mutateAsync,
    updateSupplier: updateSupplierMutation.mutateAsync,
    deleteSupplier: deleteSupplierMutation.mutateAsync,

    // Loading states
    isCreating: createSupplierMutation.isPending,
    isUpdating: updateSupplierMutation.isPending,
    isDeleting: deleteSupplierMutation.isPending,
  };
}
